interface IHeapIndex {
  [key: string]: number;
}

/**
 * Heap data structure
 */
export class IndexedHeap<T> {
  /**
   * Heap items
   */
  protected items: Array<T>;
  protected index: IHeapIndex = {};

  /**
   * Creates a new instance of heap
   *
   * @param compare function used to determine the order of the items
   * @param items (unordered) array of initial heap items
   */
  public constructor(
    private compare: (a: T, b: T) => number,
    private getKey: (a: T) => string,
    items: Array<T> = []
  ) {
    this.items = [];
    items.forEach(item => this.insert(item));
  }

  /**
   * Returns the current number of items
   */
  public get count(): number {
    return this.items.length;
  }

  public clone(): IndexedHeap<T> {
    return new IndexedHeap<T>(this.compare, this.getKey, this.items.slice());
  }

  /**
   * Inserts a new item into the heap, checking the index for existing records and replacing a value 
   *
   * @param item item to insert
   */
  public insert(item: T): void {
    const key = this.getKey(item);
    const i = this.index[key];
    if (i >= 0) {
      const existing = this.items[i];
      this.items[i] = item;
      if (this.compare(existing, item) > 0) { // If the child should replace the parent, we need to traverse swaps to the top of the heap
        this.heapUp(i);
      } else { // If the parent is properly positioned, we need to traverse down the heap to the bottom until it's positioned correctly.
        this.heapDown(i);
      }
    } else {
      const j = this.items.length;
      this.items[j] = item;
      this.index[key] = j;
      this.heapUp(j);
    }

  }

  public find(key: string): T {
    const i = this.index[key];
    return this.items[i];
  }

  public delete(item: T): void {
    const items = this.items;
    const l = items.length - 1;
    const key = this.getKey(item);
    const i = this.index[key];
    if (i !== l) {
      this.swap(items[i], items[l]);
      //Update the swapped items position from the end of the array to the item it replaced
    }

    items.pop(); // now the indexed item is on the end and we pop it off.
    delete this.index[key];

    if (i !== l)
      this.heapDown(i);
  }

  public swap(a: T, b: T) {
    const aKey = this.getKey(a);
    const bKey = this.getKey(b);

    const aPos = this.index[aKey];
    const bPos = this.index[bKey];
    [this.items[aPos], this.items[bPos]] = [this.items[bPos], this.items[aPos]];//swap the parent and current record

    this.index[aKey] = bPos;
    this.index[bKey] = aPos;

    return [bPos, aPos];
  }

  // 
  private heapUp(c: number): number {
    let p = (c - 1) / 2 >> 0; //shift off decimal bits ~ Math.floor(); find the parent index
    // While current item is not the root node and parent is found to be out of order (<0) 
    while (c > 0 && this.compare(this.items[p], this.items[c]) > 0) {
      this.swap(this.items[c], this.items[p]); //swap the parent and current record
      c = p; // set the current record to the parent index to check against the next parent
      p = (c - 1) / 2 >> 0; //find the next parent and round off the decimals
    }

    return c;
  }

  private heapDown(i: number) {
    let c, p: number;
    const n = this.items.length;
    while (true) {
      c = 2 * i + 1; //left child
      p = i; // parent index
      if (c < n && this.compare(this.items[c], this.items[p]) < 0) {
        p = c; //if parent is out of order from left child, set new index to left child
      }

      c++;
      if (c < n && this.compare(this.items[c], this.items[p]) < 0) {
        p = c; //if parent is out of order from right child, set new index to right child
      }

      if (p === i) break; // if we didn't move the record from it's current position, the heap is correct

      this.swap(this.items[i], this.items[p]);
      i = p; // move the current index to the new index and check the next children
    }
  }

  /**
   * Retrieves the largest/smallest item
   * (depending on the comparison function)
   */
  public pop(): T | null {
    const { items } = this;
    if (items.length === 0) {
      return null;
    }

    const result = items[0];
    if (items.length > 1) {
      this.swap(items[0], items[items.length - 1]);
    }

    items.length--;
    if (items.length < 2) {
      return result;
    }

    this.heapDown(0);
    return result;
  }

  public first(): T | null {
    const { items } = this;
    if (items.length === 0) {
      return null;
    }
    return items[0];
  }

  /**
   * The bottom of the tree is where we need to find our 
   * @returns
   */
  public last() {
    const n = this.items.length;
    if (!n) return null;

    const middle = Math.floor(n / 2);
    let lastItem: T = this.items[middle];
    let i = Math.floor(1 + n / 2);

    for (i; i < n; ++i) {
      const result = this.compare(lastItem, this.items[i]);
      lastItem = result < 0 ? this.items[i] : lastItem;
    }

    return lastItem;
  }


  public take(take?: number, skip?: number) {
    const copy = this.clone();
    let data: T[] = [];
    let item: T | null;
    let count = 0;
    while ((item = copy.pop()) !== null) {
      const over = (take && count >= take);
      if (over) {
        break;
      }

      if (!skip || skip >= count) {
        data.push(item);
      }
      count++;
    }
    return data;
  }

  public all(): T[] {
    return this.take();
  }
}
